// ABOUTME: Sequences privileged WhatsApp pre-registration settings requests.
// ABOUTME: Reloads CAS conflicts and link mutations without implicit retries.
import { apiDirectCall } from "customHooks/useApi";
import { getWhatsappApiResponseData } from "functions/whatsappApiResponse";

import {
    buildWhatsappPreRegistrationSaveRequest,
    createWhatsappPreRegistrationSettingsState,
    isWhatsappPreRegistrationConflict
} from "./whatsappPreRegistrationSettingsModel";

const GET_CONFIG_ENDPOINT = "/whatsapp/preregistrationconfig";
const SAVE_CONFIG_ENDPOINT = "/whatsapp/savepreregistrationconfig";
const LIST_LINKS_ENDPOINT = "/whatsapp/preregistrationstorelinks";
const ROTATE_LINK_ENDPOINT = "/whatsapp/rotatepreregistrationstorelink";
const REVOKE_LINK_ENDPOINT = "/whatsapp/revokepreregistrationstorelink";
const UPDATE_COMPANY_SETUP_ENDPOINT = "/company/updatecompanysetup";

type SettingsApiCall = (
    endpoint: string,
    verb: "post",
    body: Record<string, any>
) => Promise<any>;

interface CompanyRequest {
    companyId: number;
    call?: SettingsApiCall;
}

export const updateWhatsappPreRegistrationRollout = async ({
    companyId,
    enabled,
    call = apiDirectCall
}: CompanyRequest & { enabled: boolean }) => {
    await call(UPDATE_COMPANY_SETUP_ENDPOINT, "post", {
        companyId,
        whatsappPreRegistrationEnabled: enabled,
        operationType: ["whatsappPreRegistrationEnabled"]
    });
};

export const loadWhatsappPreRegistrationLinks = async ({
    companyId,
    call = apiDirectCall
}: CompanyRequest) => {
    const response = await call(LIST_LINKS_ENDPOINT, "post", { companyId });
    const data = getWhatsappApiResponseData(response);
    return Array.isArray(data.links) ? data.links : [];
};

const loadWhatsappPreRegistrationConfiguration = async ({
    companyId,
    call = apiDirectCall
}: CompanyRequest) => {
    const response = await call(GET_CONFIG_ENDPOINT, "post", { companyId });
    return createWhatsappPreRegistrationSettingsState(
        getWhatsappApiResponseData(response)
    );
};

export const loadWhatsappPreRegistrationSettings = async ({
    companyId,
    call = apiDirectCall
}: CompanyRequest) => {
    const [settings, links] = await Promise.all([
        loadWhatsappPreRegistrationConfiguration({ companyId, call }),
        loadWhatsappPreRegistrationLinks({ companyId, call })
    ]);

    return { settings, links };
};

const reloadWhatsappPreRegistrationSettingsAfterSave = async ({
    companyId,
    call
}: CompanyRequest) => {
    let currentSettings: ReturnType<
        typeof createWhatsappPreRegistrationSettingsState
    > | null = null;
    let currentLinks: any[] | null = null;

    for (
        let attempt = 0;
        attempt < 2 && (!currentSettings || !currentLinks);
        attempt += 1
    ) {
        const [settingsResult, linksResult] = await Promise.allSettled([
            currentSettings
                ? Promise.resolve(currentSettings)
                : loadWhatsappPreRegistrationConfiguration({ companyId, call }),
            currentLinks
                ? Promise.resolve(currentLinks)
                : loadWhatsappPreRegistrationLinks({ companyId, call })
        ]);
        if (settingsResult.status === "fulfilled") {
            currentSettings = settingsResult.value;
        }
        if (linksResult.status === "fulfilled") {
            currentLinks = linksResult.value;
        }
    }

    return { settings: currentSettings, links: currentLinks };
};

export const saveWhatsappPreRegistrationSettings = async ({
    companyId,
    settings,
    call = apiDirectCall
}: CompanyRequest & {
    settings: ReturnType<typeof createWhatsappPreRegistrationSettingsState>;
}) => {
    try {
        const response = await call(
            SAVE_CONFIG_ENDPOINT,
            "post",
            buildWhatsappPreRegistrationSaveRequest(
                companyId,
                settings.config,
                settings.revision
            )
        );
        const data = getWhatsappApiResponseData(response);
        const savedSettings = createWhatsappPreRegistrationSettingsState({
            config: data.config,
            options: settings.options
        });
        const current = await reloadWhatsappPreRegistrationSettingsAfterSave({
            companyId,
            call
        });
        return {
            status: "saved" as const,
            settings: current.settings || savedSettings,
            links: current.links || []
        };
    } catch (error) {
        if (!isWhatsappPreRegistrationConflict(error)) {
            throw error;
        }
        const current = await loadWhatsappPreRegistrationSettings({
            companyId,
            call
        });
        return { status: "conflict" as const, ...current };
    }
};

const updateWhatsappPreRegistrationLink = async ({
    endpoint,
    companyId,
    storeId,
    call
}: CompanyRequest & { endpoint: string; storeId: string }) => {
    const request = call || apiDirectCall;
    await request(endpoint, "post", { companyId, storeId });
    return loadWhatsappPreRegistrationLinks({ companyId, call: request });
};

export const rotateWhatsappPreRegistrationLink = ({
    companyId,
    storeId,
    call
}: CompanyRequest & { storeId: string }) =>
    updateWhatsappPreRegistrationLink({
        endpoint: ROTATE_LINK_ENDPOINT,
        companyId,
        storeId,
        call
    });

export const revokeWhatsappPreRegistrationLink = ({
    companyId,
    storeId,
    call
}: CompanyRequest & { storeId: string }) =>
    updateWhatsappPreRegistrationLink({
        endpoint: REVOKE_LINK_ENDPOINT,
        companyId,
        storeId,
        call
    });
